import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';

type PauseResumeResponse = {
  status: string;
  promotion: any;
};

const usePausePromotionFn = () => {
  const request = useClientStore((s) => s.request);

  return withErrorHandling(
    async (promid: number, reason?: string): Promise<PauseResumeResponse> => {
      const payload = reason ? { reason } : {};
      const response = await request<PauseResumeResponse>(
        `/api/admin/promotions/${promid}/pause`,
        'POST',
        payload,
      );
      return response;
    },
    'Failed to pause promotion',
  );
};

const useResumePromotionFn = () => {
  const request = useClientStore((s) => s.request);

  return withErrorHandling(
    async (promid: number): Promise<PauseResumeResponse> => {
      const response = await request<PauseResumeResponse>(
        `/api/admin/promotions/${promid}/resume`,
        'POST',
      );
      return response;
    },
    'Failed to resume promotion',
  );
};

export const usePausePromotionMutation = () => {
  const queryClient = useQueryClient();
  const pauseFn = usePausePromotionFn();

  return useMutation({
    mutationFn: ({ promid, reason }: { promid: number; reason?: string }) =>
      pauseFn(promid, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
    },
  });
};

export const useResumePromotionMutation = () => {
  const queryClient = useQueryClient();
  const resumeFn = useResumePromotionFn();

  return useMutation({
    mutationFn: ({ promid }: { promid: number }) => resumeFn(promid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
    },
  });
};
